import React from 'react';
import { Chip } from '@heroui/react';
import type { IntentRule, TargetType, MatchType } from '@/types';

/** Rule shape from API - intent format or legacy { name, css_selector } */
export interface DisplayRule {
  name?: string;
  css_selector?: string;
  regex_selector?: { attribute: string; pattern: string } | null;
  target_type?: TargetType;
  match_type?: MatchType | null;
  attribute_name?: string | null;
  target_value?: string;
}

const TARGET_TYPE_LABELS: Record<TargetType, string> = {
  css: 'CSS Selector',
  text: 'Element Text',
  attribute: 'HTML Attribute',
};

const MATCH_TYPE_LABELS: Record<MatchType, string> = {
  exact: 'Exact Match',
  contains: 'Contains',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  custom: 'Custom Regex',
};

function getDisplayFromRule(rule: DisplayRule): {
  targetBy: string;
  matchType: string | null;
  value: string;
} {
  const targetType = rule.target_type;
  const matchType = rule.match_type;

  if (targetType && rule.target_value != null && rule.target_value !== '') {
    return {
      targetBy: TARGET_TYPE_LABELS[targetType as TargetType] ?? targetType,
      matchType: matchType ? (MATCH_TYPE_LABELS[matchType as MatchType] ?? matchType) : null,
      value:
        targetType === 'attribute' && rule.attribute_name
          ? `${rule.attribute_name}: ${rule.target_value}`
          : rule.target_value,
    };
  }

  if (rule.regex_selector) {
    const { attribute, pattern } = rule.regex_selector;
    return {
      targetBy: 'HTML Attribute',
      matchType: 'Custom Regex',
      value: attribute ? `${attribute}: ${pattern}` : pattern,
    };
  }

  if (rule.css_selector) {
    return {
      targetBy: 'CSS Selector',
      matchType: null,
      value: rule.css_selector,
    };
  }

  return {
    targetBy: 'CSS Selector',
    matchType: null,
    value: '(empty)',
  };
}

interface SelectorReadOnlyViewProps {
  rule: DisplayRule;
  color?: 'success' | 'secondary' | 'default';
  className?: string;
}

export function SelectorReadOnlyView({ rule, color = 'success', className = '' }: SelectorReadOnlyViewProps) {
  const { targetBy, matchType, value } = getDisplayFromRule(rule);
  const name = rule.name?.trim();

  return (
    <div
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50 ${className}`}
    >
      {name && (
        <span className="text-default-700 font-medium">{name}</span>
      )}
      <Chip size="sm" variant="flat" color={color} className="font-mono text-xs">
        {targetBy}
      </Chip>
      {matchType && (
        <Chip size="sm" variant="flat" color="default" className="font-mono text-xs">
          {matchType}
        </Chip>
      )}
      <span className="font-mono text-xs text-default-600 dark:text-default-400 break-all">{value}</span>
    </div>
  );
}
